import { useState, useMemo } from "react";

function getDomain(url) {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

// Some domains where Clearbit returns a poor result — go straight to Google favicon.
const FAVICON_PREFERRED = new Set([
  "reddit.com",
  "slickdeals.net",
]);

const bgTints = [
  "#ffb0a7",
  "#f7c873",
  "#4ecdc4",
  "#c89ec4",
  "#6ec5e9",
  "#7dbb6e",
  "#ff6b9d",
  "#b6a6de",
  "#ffa17f",
  "#b8d8be",
];

function pickTint(store) {
  let h = 0;
  for (let i = 0; i < (store || "").length; i++) h = (h * 31 + store.charCodeAt(i)) | 0;
  return bgTints[Math.abs(h) % bgTints.length];
}

export default function StoreLogo({ store, storeUrl, size = 44, className = "", testid }) {
  const domain = getDomain(storeUrl);
  const sources = useMemo(() => {
    if (!domain) return [];
    // logo.dev delivers HD brand logos (transparent PNG) — token is a public
    // publishable key, safe to expose. Request max-resolution so retina
    // displays stay crisp when we scale down to card/thumbnail sizes.
    const LOGODEV = "pk_X-1zO13GSgeOoUrIuJ6GMQ";
    return [
      `https://img.logo.dev/${domain}?token=${LOGODEV}&size=400&format=png&retina=true`,
      // Google favicons API max resolution is 256 — anything smaller gets upscaled and looks fuzzy.
      `https://www.google.com/s2/favicons?domain=${domain}&sz=256`,
    ];
  }, [domain]);

  const [srcIdx, setSrcIdx] = useState(0);
  const tint = pickTint(store);
  const initials = (store || "?")
    .split(/\s|&/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const boxStyle = {
    width: size,
    height: size,
    minWidth: size,
    borderRadius: 12,
    background: "#ffffff",
  };

  if (!domain || srcIdx >= sources.length) {
    return (
      <div
        data-testid={testid}
        className={`flex items-center justify-center border-2 border-[#2d2a32] font-black serif text-[#2d2a32] shrink-0 ${className}`}
        style={{ ...boxStyle, background: tint, fontSize: size * 0.42 }}
        title={store}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`flex items-center justify-center border-2 border-[#2d2a32] shrink-0 overflow-hidden ${className}`}
      style={{ ...boxStyle, background: "#ffffff" }}
      title={store}
    >
      <img
        src={sources[srcIdx]}
        alt={`${store} logo`}
        onError={() => setSrcIdx((i) => i + 1)}
        loading="lazy"
        decoding="async"
        width={Math.round(size * 0.78 * 2)}
        height={Math.round(size * 0.78 * 2)}
        style={{
          width: "78%",
          height: "78%",
          objectFit: "contain",
          imageRendering: "-webkit-optimize-contrast",
        }}
      />
    </div>
  );
}
